import React, { useState } from 'react';
import moment from 'moment';
import jsPDF from 'jspdf';
import { Drawer, Badge, Button } from './ui'
import ImageModal from './ImageModal'

const apiURL = process.env.REACT_APP_BACKEND

// [86bbumedw] P5 — fetch an image URL → data URL so jsPDF can embed it. /uploads is CORS-enabled
// (the cors middleware runs before the static route), so the cross-origin fetch works. Best-effort:
// returns null on any failure so the PDF still renders (text + whatever photos did load).
function urlToDataURL(url) {
  return fetch(url)
    .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('HTTP ' + r.status))))
    .then((blob) => new Promise((resolve, reject) => {
      const fr = new FileReader()
      fr.onload = () => resolve(fr.result)
      fr.onerror = reject
      fr.readAsDataURL(blob)
    }))
    .catch(() => null)
}
// natural dimensions of a data-url image (to scale into the PDF without distortion)
function imgDims(dataURL) {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 })
    img.onerror = () => resolve({ w: 1, h: 1 })
    img.src = dataURL
  })
}
// Build a self-contained PDF of one crossing: details + embedded photos (default: photos embedded,
// so the record is a single shareable file). jsPDF 1.5.3.
async function exportEventPdf(event, photos, ts) {
  const doc = new jsPDF('p', 'pt', 'letter') // 612 x 792 pt
  const size = doc.internal.pageSize
  const pageW = size.getWidth ? size.getWidth() : size.width
  const pageH = size.getHeight ? size.getHeight() : size.height
  const M = 42
  let y = M
  doc.setFontSize(15); doc.text('GateWatcher — Crossing Record', M, y); y += 22
  doc.setFontSize(10)
  const row = (label, val) => {
    doc.setTextColor(120); doc.text(String(label), M, y)
    doc.setTextColor(25); doc.text(String(val == null || val === '' ? '—' : val), M + 112, y)
    y += 16
  }
  const paxNames = (event.passengers || []).map((p) => `${p.sFirstName} ${p.sLastName}`).join(', ')
  row('Event #', event.id)
  row('Type', event.GateType && event.GateType.sName)
  row('Date / Time', ts && ts.isValid() ? ts.format('MMMM DD, YYYY hh:mm a') : '')
  row('Gate', (event.Gate && event.Gate.sName) || 'UNASSIGNED')
  row('Customer', (event.GateCustomer && event.GateCustomer.sName) || 'UNASSIGNED')
  row('Company', event.GateCompany && event.GateCompany.sName)
  row('Driver', event.GatePerson && event.GatePerson.sFullName)
  row('License Plate', event.GateLPN && event.GateLPN.sName)
  row('Passengers', paxNames || (event.bPassengerCount > 0 ? event.bPassengerCount : '—'))
  row('Logged By', event.GateUser && event.GateUser.sFullName)
  if (event.sComment) {
    doc.setTextColor(120); doc.text('Note', M, y)
    doc.setTextColor(25)
    const wrapped = doc.splitTextToSize(String(event.sComment), pageW - (M + 112) - M)
    doc.text(wrapped, M + 112, y); y += 16 * wrapped.length
  }
  y += 10
  for (const ph of photos) {
    const data = await urlToDataURL(apiURL + ph.path)
    if (!data) continue
    const d = await imgDims(data)
    const maxW = Math.min(pageW - M * 2, 460)
    let w = maxW, h = (w * d.h) / d.w
    const maxH = pageH - M * 2 - 40
    if (h > maxH) { h = maxH; w = (h * d.w) / d.h }
    if (y + h + 20 > pageH - M) { doc.addPage(); y = M }
    doc.setFontSize(9); doc.setTextColor(120); doc.text(ph.label, M, y); y += 12
    const fmt = data.indexOf('image/png') !== -1 ? 'PNG' : 'JPEG'
    try { doc.addImage(data, fmt, M, y, w, h) } catch (e) { /* skip an unembeddable image */ }
    y += h + 18
  }
  doc.save(`gw-event-${event.id}.pdf`)
}

const TYPE_VARIANT = { IN: 'in', OUT: 'out', DENIED: 'denied', ACCIDENT: 'accident', LOGIN: 'auth', LOGOUT: 'auth' }

// Parse the crossing's photos from the SAME fields the PDF viewer used:
// sLpnPhoto, sLoadPhoto, and sImages (a comma-separated list of upload paths).
// Read-only — no new API, no writes.
function collectPhotos(event) {
  if (!event) return []
  const photos = []
  if (event.sLpnPhoto) photos.push({ label: 'License Plate', path: event.sLpnPhoto })
  if (event.sLoadPhoto) photos.push({ label: 'Load', path: event.sLoadPhoto })
  if (event.sImages && event.sImages.length > 0) {
    const parts = event.sImages.split(',')
    // as of 20240613 sImages is just comma-separated path values; older rows
    // used '~' / '-' separators (kept for parity with EventPdf.parseImages).
    parts.forEach((raw, index) => {
      let path = raw
      if (!raw.startsWith('uploads')) {
        if (raw.includes('~')) path = raw.split('~')[1]
        else if (raw.includes('-')) { const s = raw.split('-'); path = s[1] + '-' + s[2] }
      }
      if (path) photos.push({ label: 'Photo ' + (index + 1), path })
    })
  }
  return photos
}

/*
 * EventDrawer — read-only Console drawer for a single crossing.
 * Presentation of data already on the row/event (no fetch). Photos are adaptive:
 * a gallery when the crossing has any photo fields, else the "No photos" notice.
 */
export default function EventDrawer({ event, onClose }) {
  const [ preview, setPreview ] = useState(null)  // { imageURI, label }
  const [ exporting, setExporting ] = useState(false)  // [86bbumedw] P5 PDF export in progress

  const photos = collectPhotos(event)
  const name = event?.GateType?.sName
  const ts = event?.timestamp && event.timestamp.length
    ? moment(event.timestamp)
    : moment(event?.createdAt)

  const header = event ? (
    <div className="grow">
      <div className="eyebrow">Event #{ event.id }</div>
      <div className="row" style={{ gap: 8, marginTop: 4 }}>
        { name && <Badge variant={TYPE_VARIANT[name] || 'neutral'}>{ name }</Badge> }
        <span className="mono" style={{ fontSize: 13, color: 'var(--text-2)' }}>
          { ts.isValid() ? ts.format('MM/DD/YY · hh:mm a') : '' }
        </span>
      </div>
    </div>
  ) : null

  return (
    <>
      <Drawer
        open={!!event}
        onClose={onClose}
        header={header}
        className="gw-monitor-drawer"
        width={photos.length >= 3 ? 'min(680px, 96vw)' : undefined}
        footer={event &&
          <Button
            variant="primary"
            loading={exporting}
            onClick={async () => {
              setExporting(true)
              try { await exportEventPdf(event, photos, ts) } catch (e) { console.error('PDF export failed', e) }
              setExporting(false)
            }}
          >
            Export PDF
          </Button>
        }
      >
        { event &&
          <>
            <div className="section-label"><span className="eyebrow">Crossing</span></div>
            <dl className="kv">
              <dt>Gate</dt><dd>{ event.Gate?.sName || 'UNASSIGNED' }</dd>
              <dt>Customer</dt><dd>{ event.GateCustomer?.sName || 'UNASSIGNED' }</dd>
              <dt>Company</dt><dd>{ event.GateCompany?.sName || '—' }</dd>
              <dt>Driver</dt><dd>{ event.GatePerson?.sFullName || '—' }</dd>
              <dt>License Plate</dt><dd className="mono">{ event.GateLPN?.sName || '—' }</dd>
              <dt>Passengers</dt><dd className="num">{ (event.bPassengerCount && parseInt(event.bPassengerCount) > 0) ? event.bPassengerCount : '—' }</dd>
              <dt>Logged By</dt><dd style={{ textTransform: 'capitalize' }}>{ event.GateUser?.sFullName || '—' }</dd>
            </dl>

            { event.passengers && event.passengers.length > 0 &&
              <>
                <div className="section-label"><span className="eyebrow">Passengers</span></div>
                <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 13.5, lineHeight: 1.5 }}>
                  { event.passengers.map(p => `${p.sFirstName} ${p.sLastName}`).join(', ') }
                </p>
              </>
            }

            { event.sComment &&
              <>
                <div className="section-label"><span className="eyebrow">Note</span></div>
                <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 13.5, lineHeight: 1.5 }}>{ event.sComment }</p>
              </>
            }

            <div className="section-label"><span className="eyebrow">Photos</span></div>
            { photos.length > 0
                ? <div className="photos">
                    { photos.map((p, i) => (
                      <div key={i} className="photo" title={p.label} onClick={() => setPreview({ imageURI: p.path, label: p.label })}>
                        <img src={ apiURL + p.path } alt={ p.label } />
                      </div>
                    )) }
                  </div>
                : <div className="nophoto">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="12" cy="12" r="3" /><path d="M4 5 20 19" />
                    </svg>
                    No photos attached to this crossing.
                  </div>
            }
          </>
        }
      </Drawer>

      { preview &&
        <ImageModal
          closeImageModal={() => setPreview(null)}
          imageURI={ preview.imageURI }
          timestamp={ ts.isValid() ? ts.format('MMMM DD, YYYY hh:mm a') : '' }
          currentImageLabel={ preview.label }
        />
      }
    </>
  )
}
